import React, { useState, useEffect } from 'react';
import { Shield, Clock, Plus, CheckCircle, AlertCircle } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import styles from './Patrolling.module.css';

const Patrolling = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [patrols, setPatrols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPatrols();
  }, []);

  const fetchPatrols = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/patrols');
      setPatrols(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ route: 'Perimeter Check', guard: '', notes: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/security/patrols', formData);
      setPatrols([data, ...patrols]);
      setIsDrawerOpen(false);
      setFormData({ route: 'Perimeter Check', guard: '', notes: '' });
      toast.success('Patrol started successfully!');
    } catch (error) {
      toast.error('Failed to start patrol');
    }
  };

  const handleEndPatrol = async (id) => {
    try {
      const { data } = await api.put(`/security/patrols/${id}/end`);
      setPatrols(patrols.map(p => p._id === id ? data : p));
      toast.success('Patrol ended successfully!');
    } catch (error) {
      toast.error('Failed to end patrol');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Patrolling Logs</h1>
          <p>Monitor security patrol rounds across the society</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <Plus size={16} /> Start New Round
        </button>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span>Active Rounds</span>
          <h2>{patrols.filter(p => p.status === 'In Progress').length}</h2>
        </div>
        <div className={styles.statCard}>
          <span>Completed Today</span>
          <h2>{patrols.filter(p => p.status === 'Completed').length}</h2>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Round ID</th>
                <th>Route / Area</th>
                <th>Assigned Guard</th>
                <th>Time (Start - End)</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : patrols.map(p => (
                <tr key={p._id}>
                  <td><strong>{p._id.substring(p._id.length - 6).toUpperCase()}</strong></td>
                  <td>
                    <div className={styles.routeInfo}>
                      <Shield size={16} className={styles.iconShield} />
                      {p.route}
                    </div>
                  </td>
                  <td>{p.guard}</td>
                  <td>
                    <div className={styles.timeInfo}>
                      <Clock size={12} />
                      {new Date(p.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                      - 
                      {p.endTime ? new Date(p.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[p.status.replace(/\s+/g, '').toLowerCase()]}`}>
                      {p.status}
                    </span>
                  </td>
                  <td><span className={styles.notesText}>{p.notes || '-'}</span></td>
                  <td>
                    {p.status === 'In Progress' && (
                      <button className={styles.btnAction} onClick={() => handleEndPatrol(p._id)}>
                        <CheckCircle size={14} /> End Round
                      </button>
                    )}
                    {p.status === 'Completed' && <span className={styles.textMuted}>Logged</span>}
                    {p.status === 'Scheduled' && <span className={styles.textMuted}>Waiting</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <p style={{padding: '16px', textAlign: 'center'}}>Loading...</p> : patrols.map(p => (
              <div key={p._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.routeInfo}>
                    <Shield size={16} className={styles.iconShield} />
                    <strong>{p.route}</strong>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles[p.status.replace(/\s+/g, '').toLowerCase()]}`}>
                    {p.status}
                  </span>
                </div>
                <div className={styles.cardMid}>
                  <p><strong>Guard:</strong> {p.guard}</p>
                  <div className={styles.timeInfo}>
                    <Clock size={12} />
                    {new Date(p.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                    - 
                    {p.endTime ? new Date(p.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </div>
                  {p.notes && <p className={styles.notesText}><strong>Notes:</strong> {p.notes}</p>}
                </div>
                <div className={styles.cardBottom}>
                  {p.status === 'In Progress' && (
                    <button className={styles.btnAction} style={{width: '100%', justifyContent: 'center'}} onClick={() => handleEndPatrol(p._id)}>
                      <CheckCircle size={14} /> End Round
                    </button>
                  )}
                  {p.status === 'Completed' && <span className={styles.textMuted} style={{width: '100%', textAlign: 'center', padding: '6px 0'}}>Logged</span>}
                  {p.status === 'Scheduled' && <span className={styles.textMuted} style={{width: '100%', textAlign: 'center', padding: '6px 0'}}>Waiting</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Start Patrol Round">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Assigned Guard</label>
            <input name="guard" value={formData.guard} onChange={handleChange} required placeholder="Guard Name" />
          </div>
          <div className="formGroup">
            <label>Patrol Route / Area</label>
            <select name="route" value={formData.route} onChange={handleChange}>
              <option value="Perimeter Check">Perimeter Check</option>
              <option value="Basement Parking">Basement Parking</option>
              <option value="Clubhouse & Amenities">Clubhouse & Amenities</option>
              <option value="Tower A Floors">Tower A Floors</option>
              <option value="Tower B Floors">Tower B Floors</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Initial Notes (Optional)</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" placeholder="Any specific instructions..."></textarea>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Start Round</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Patrolling;
